/**
 * Labels for the employment facts a recruiter sets on a job.
 *
 * `type` and `placement` are free-text columns shared by theResume and theRecruit, and the
 * values in them have drifted: theRecruit's job settings writes FULL_TIME / PART_TIME /
 * CONTRACT / INTERNSHIP / FREELANCE / TEMPORARY / CONSULTANT, while older rows still carry the
 * legacy `fulltime` / `contractor` spellings. Anything that maps an unrecognised value onto a
 * default is guessing at the employer's terms on their behalf — which is how a FULL_TIME role
 * ended up advertised to candidates as "Contract".
 *
 * So: recognised values get their canonical label, unrecognised-but-present values are shown as
 * written (tidied up), and absent values return null so the caller can omit the field entirely
 * rather than invent one.
 */

/** Uppercase, underscore-separated form, so "full-time", "Full Time" and "FULL_TIME" all agree. */
const canonicalKey = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");

/** "SOMETHING_NEW" → "Something New", for values added upstream that we don't know yet. */
const humanize = (value: string) =>
  canonicalKey(value)
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const JOB_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: "Full Time",
  PART_TIME: "Part Time",
  CONTRACT: "Contract",
  INTERNSHIP: "Internship",
  FREELANCE: "Freelance",
  TEMPORARY: "Temporary",
  CONSULTANT: "Consultant",
  // Legacy spellings still stored on jobs created before theRecruit's job settings moved to the
  // list above. They collapse to the same labels rather than to a different meaning.
  FULLTIME: "Full Time",
  PARTTIME: "Part Time",
  CONTRACTOR: "Contract",
  INTERN: "Internship",
};

const WORK_ARRANGEMENT_LABELS: Record<string, string> = {
  ONSITE: "On-site",
  ON_SITE: "On-site",
  IN_OFFICE: "On-site",
  REMOTE: "Remote",
  HYBRID: "Hybrid",
};

const labelFrom = (labels: Record<string, string>, value?: string | null): string | null => {
  if (typeof value !== "string" || value.trim().length === 0) return null;
  return labels[canonicalKey(value)] ?? humanize(value) ?? null;
};

/** Employment type as the recruiter set it, or null when the job doesn't specify one. */
export const formatJobType = (value?: string | null): string | null =>
  labelFrom(JOB_TYPE_LABELS, value);

/** Work arrangement (on-site / remote / hybrid), or null when the job doesn't specify one. */
export const formatWorkArrangement = (value?: string | null): string | null =>
  labelFrom(WORK_ARRANGEMENT_LABELS, value);
